import { I18n } from "@/i18n/I18n";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useProjectStore } from "@/frontend/store/ProjectStore";
import { useShallow } from "zustand/react/shallow";
import { SearchList } from "../SearchList";
import { ProjectRequirementCard } from "./ProjectRequirementCard";
import { ProjectRequirementActions } from "./ProjectRequirementActions";
import { NotFound } from "@/frontend/pages/NotFound";
import { ContainersPlaceholder } from "../containers/ContainersPlaceholder";
import { Requirement } from "@/backend/models/requirement";
import { RequirementSubjectType } from "@/backend/access/requirement/types";
import { useRequirementsStore } from "@/frontend/store/RequirementsStore";

export function ProjectRequirementsList() {
  const projectId = useParams().projectId;
  const project = useProjectStore(useShallow(state => state.projects.find(project => project.id === projectId)));
  const assignedRequirements = useRequirementsStore(useShallow(state => state.requirements
    .filter(requirement => requirement.subjectId === projectId && requirement.subjectType === RequirementSubjectType.PROJECT)
    .sort((a) => a.applicable ? -1 : 1)
  ));
  
  const fetchProjectRequirements = useRequirementsStore(state => state.fetchRequirementsBySubject);
  
  const [loading, setLoading] = useState(true);
  const [requirementList, setRequirementList] = useState<Requirement[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (projectId) {
        await Promise.all([
          fetchProjectRequirements(projectId, RequirementSubjectType.PROJECT)
        ]);
        setLoading(false);
      }
    };
    void fetchData();
  }, [fetchProjectRequirements]);

  useEffect(() => {
    if (assignedRequirements) {
      setRequirementList(assignedRequirements);
    }
  }, [assignedRequirements]);

  if (!projectId || !project) {
    return <NotFound />;
  }

  return (
    <div className="p-3">
      <div className="pb-3">
        <div className="d-flex gap-2 align-items-center">
          <h1 className="border-end pe-2">{I18n.get('requirements')}</h1>
          <div className="d-flex gap-2 align-items-center">
            <ProjectRequirementActions projectId={projectId} />
          </div>
        </div>
      </div>
      <div>
        <SearchList 
          items={assignedRequirements ?? []} 
          setItems={setRequirementList} 
          modelLabel={I18n.get('requirement')}
        >
        </SearchList>
        <div className="mt-3">
          <RequirementsList loading={loading} requirements={requirementList} />
        </div>
      </div>
    </div>
  );
}

function RequirementsList(props: { loading: boolean, requirements: Requirement[] }) {
  const { loading, requirements } = props;

  if (loading) {
    return <ContainersPlaceholder />;
  }

  if(requirements.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">{I18n.get('noRequirementsAssigned')}</p>
      </div>
    );
  }

  return (
    <div className="list-card-block">
      {requirements.map((requirement) => (
        <ProjectRequirementCard
          key={requirement.id}
          requirement={requirement}
        />
      ))}
    </div>
  );
}
